import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Divider,
  TextField,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Whatshot,
  TrendingUp,
  CheckCircle,
  Schedule,
  Warning
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import { formatNumber, formatCurrency } from '../utils/formatters';

const BurnPool = () => {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [transactionHash, setTransactionHash] = useState('');

  // Состояния для ручного сжигания
  const [manualBurnDialog, setManualBurnDialog] = useState(false);
  const [burnAmount, setBurnAmount] = useState('');
  const [walletBalance, setWalletBalance] = useState(0);

  // Состояние для праздничного диалога успеха
  const [successDialog, setSuccessDialog] = useState(false);
  const [burnResult, setBurnResult] = useState(null);

  // Состояние для рассылки
  const [sendBroadcast, setSendBroadcast] = useState(true);
  const [broadcastProgress, setBroadcastProgress] = useState(null);

  // Состояние для списания с burn пула
  const [usePoolAmount, setUsePoolAmount] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Polling прогресса рассылки
  const pollBroadcastProgress = async (broadcastId) => {
    const interval = setInterval(async () => {
      try {
        const response = await adminAPI.getBroadcastStatus(broadcastId);
        if (response.data.success) {
          const { sent, total, status } = response.data.data;
          setBroadcastProgress({ sent, total, status });

          if (status === 'completed' || status === 'failed') {
            clearInterval(interval);
          }
        }
      } catch (error) {
        console.error('Ошибка получения прогресса рассылки:', error);
        clearInterval(interval);
      }
    }, 2000); // Проверяем каждые 2 секунды

    // Очищаем интервал через 5 минут максимум
    setTimeout(() => clearInterval(interval), 300000);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadStatus(),
        loadHistory(),
        loadStats(),
        loadWalletBalance()
      ]);
    } catch (error) {
      console.error('Ошибка загрузки данных burn pool:', error);
      setErrorMessage('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const loadWalletBalance = async () => {
    try {
      const response = await adminAPI.getWalletBalance();
      if (response.data.success) {
        setWalletBalance(response.data.data.balance || 0);
      }
    } catch (error) {
      console.error('Ошибка загрузки баланса кошелька:', error);
    }
  };

  const loadStatus = async () => {
    try {
      const response = await adminAPI.getBurnPoolStatus();
      if (response.data.success) {
        setStatus(response.data.data);
      }
    } catch (error) {
      console.error('Ошибка загрузки статуса:', error);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await adminAPI.getBurnPoolHistory({ limit: 50, offset: 0 });
      if (response.data.success) {
        setHistory(response.data.data || []);
      }
    } catch (error) {
      console.error('Ошибка загрузки истории:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await adminAPI.getBurnPoolStats();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Ошибка загрузки статистики:', error);
    }
  };

  const handleExecuteBurn = async () => {
    try {
      if (!transactionHash || transactionHash.trim() === '') {
        setErrorMessage('Требуется хэш транзакции сжигания');
        return;
      }

      setExecuting(true);
      setErrorMessage('');
      setSuccessMessage('');

      const response = await adminAPI.executeBurn({ transaction_hash: transactionHash });

      if (response.data.success) {
        setSuccessMessage(response.data.message || 'Сжигание успешно выполнено');
        setConfirmDialog(false);
        setTransactionHash(''); // Очищаем поле
        // Перезагружаем данные
        await loadData();
      } else {
        setErrorMessage(response.data.message || 'Ошибка выполнения сжигания');
      }
    } catch (error) {
      console.error('Ошибка выполнения burn:', error);
      setErrorMessage(error.response?.data?.message || 'Ошибка выполнения сжигания');
    } finally {
      setExecuting(false);
    }
  };

  // Ручное сжигание (автоматическая отправка на блокчейн)
  const handleManualBurn = async () => {
    try {
      // Если используем пул - берём сумму оттуда
      const amount = usePoolAmount ? currentBalance : parseFloat(burnAmount);

      if (!amount || amount <= 0) {
        setErrorMessage(usePoolAmount
          ? 'Burn пул пуст - нечего сжигать'
          : 'Укажите корректную сумму для сжигания');
        return;
      }

      if (amount > walletBalance) {
        setErrorMessage(`Недостаточно SPERM на кошельке. Доступно: ${walletBalance.toFixed(2)}`);
        return;
      }

      setExecuting(true);
      setErrorMessage('');
      setSuccessMessage('');

      const response = await adminAPI.executeManualBurn(amount, sendBroadcast, usePoolAmount);

      if (response.data.success) {
        const txHash = response.data.data?.txHash || '';
        const burnedAmount = response.data.data?.burned || amount;
        const broadcastId = response.data.data?.broadcastId || null;

        // Сохраняем результат для праздничного диалога
        setBurnResult({
          amount: burnedAmount,
          txHash: txHash,
          timestamp: new Date().toLocaleString('ru-RU')
        });

        // Если есть рассылка - запускаем polling прогресса
        if (broadcastId && sendBroadcast) {
          setBroadcastProgress({ sent: 0, total: 0, status: 'in_progress' });
          pollBroadcastProgress(broadcastId);
        }

        setManualBurnDialog(false);
        setBurnAmount('');
        setSuccessDialog(true); // Показываем праздничный диалог

        // Перезагружаем данные
        await loadData();
      } else {
        setErrorMessage(response.data.message || 'Ошибка выполнения сжигания');
      }
    } catch (error) {
      console.error('Ошибка ручного сжигания:', error);
      setErrorMessage(error.response?.data?.message || 'Ошибка выполнения сжигания');
    } finally {
      setExecuting(false);
    }
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 70
    },
    {
      field: 'burn_date',
      headerName: 'Дата',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleString('ru-RU')}
        </Typography>
      )
    },
    {
      field: 'amount_burned',
      headerName: 'Сумма сожжено',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Whatshot sx={{ mr: 1, color: '#ef4444', fontSize: 20 }} />
          <Typography sx={{ fontWeight: 'bold', color: '#ef4444' }}>
            {formatCurrency(params.value)}
          </Typography>
        </Box>
      )
    },
    {
      field: 'source_breakdown',
      headerName: 'Источники',
      width: 250,
      renderCell: (params) => {
        const breakdown = params.value || {};
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {breakdown.lunar && (
              <Chip
                label={`Lunar: ${formatCurrency(breakdown.lunar)}`}
                size="small"
                color="primary"
              />
            )}
            {breakdown.star && (
              <Chip
                label={`Star: ${formatCurrency(breakdown.star)}`}
                size="small"
                color="secondary"
              />
            )}
          </Box>
        );
      }
    },
    {
      field: 'transaction_hash',
      headerName: 'TX Hash',
      width: 200,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            color: '#94a3b8'
          }}
        >
          {params.value ? `${params.value.substring(0, 16)}...` : 'N/A'}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Статус',
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={params.value === 'completed' ? <CheckCircle /> : <Schedule />}
          label={params.value === 'completed' ? 'Завершено' : 'В процессе'}
          size="small"
          color={params.value === 'completed' ? 'success' : 'warning'}
        />
      )
    }
  ];

  const currentBalance = status?.current_balance || 0;
  const totalBurned = stats?.total_burned || 0;
  const lastBurnDate = status?.last_burn_date
    ? new Date(status.last_burn_date).toLocaleDateString('ru-RU')
    : 'Никогда';
  const nextBurnDate = status?.next_burn_date
    ? new Date(status.next_burn_date).toLocaleDateString('ru-RU')
    : '10-го числа';

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
        <Whatshot sx={{ mr: 2, fontSize: 40, color: '#ef4444' }} />
        Burn Pool - Пул Сжигания Токенов
      </Typography>

      {/* Сообщения */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <CardContent>
              <Typography color="white" variant="body2">
                Текущий баланс пула
              </Typography>
              <Typography color="white" variant="h5" sx={{ fontWeight: 'bold', my: 1 }}>
                {formatCurrency(currentBalance)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min((currentBalance / 1000) * 100, 100)}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  '& .MuiLinearProgress-bar': { backgroundColor: 'white' }
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <CardContent>
              <Typography color="white" variant="body2">
                Всего сожжено
              </Typography>
              <Typography color="white" variant="h5" sx={{ fontWeight: 'bold', my: 1 }}>
                {formatCurrency(totalBurned)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={75}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  '& .MuiLinearProgress-bar': { backgroundColor: 'white' }
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <CardContent>
              <Typography color="white" variant="body2">
                Последнее сжигание
              </Typography>
              <Typography color="white" variant="h5" sx={{ fontWeight: 'bold', my: 1 }}>
                {lastBurnDate}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={100}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  '& .MuiLinearProgress-bar': { backgroundColor: 'white' }
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
            <CardContent>
              <Typography color="white" variant="body2">
                Следующее сжигание
              </Typography>
              <Typography color="white" variant="h5" sx={{ fontWeight: 'bold', my: 1 }}>
                {nextBurnDate}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={50}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  '& .MuiLinearProgress-bar': { backgroundColor: 'white' }
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Информация о burn pool */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          📊 Информация о пуле
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              <strong>Что такое Burn Pool?</strong>
            </Typography>
            <Typography variant="body2" paragraph>
              Пул сжигания накапливает токены из различных источников (штрафы за досрочный вывод,
              комиссии за стейкинги) и ежемесячно сжигает их, уменьшая общее предложение токенов.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              <strong>Источники пополнения:</strong>
            </Typography>
            <Typography variant="body2">
              • Lunar Staking: 10% при создании<br/>
              • Star Staking: 5% при создании<br/>
              • Штрафы за досрочный вывод Star Staking<br/>
              • Сжигание автоматическое: 10-го числа каждого месяца
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Кнопки выполнения сжигания */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Баланс кошелька */}
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(99, 102, 241, 0.1)', borderRadius: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Баланс SPERM на кошельке
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#6366f1' }}>
                {formatCurrency(walletBalance)}
              </Typography>
            </Box>
          </Grid>

          {/* Кнопка ручного сжигания */}
          <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<Whatshot />}
              onClick={() => {
                setBurnAmount('');
                setUsePoolAmount(false);
                setManualBurnDialog(true);
              }}
              disabled={walletBalance === 0 || executing}
              sx={{
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                }
              }}
            >
              {executing ? 'Выполняется...' : 'СЖЕЧЬ ТОКЕНЫ'}
            </Button>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
              Автоматическая отправка burn транзакции
            </Typography>
          </Grid>

          {/* Кнопка сжигания пула (старый способ) */}
          <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<Whatshot />}
              onClick={() => setConfirmDialog(true)}
              disabled={currentBalance === 0 || executing}
              sx={{
                borderColor: '#94a3b8',
                color: '#94a3b8',
                px: 3,
                py: 1,
                '&:hover': {
                  borderColor: '#ef4444',
                  color: '#ef4444',
                }
              }}
            >
              Сжечь пул (с хэшем)
            </Button>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
              Пул: {formatCurrency(currentBalance)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Таблица истории */}
      <Paper sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={history}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          loading={loading}
          getRowId={(row) => row.id}
          sx={{
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#1e293b',
              borderBottom: '2px solid #6366f1'
            }
          }}
        />
      </Paper>

      {/* Диалог подтверждения */}
      <Dialog
        open={confirmDialog}
        onClose={() => !executing && setConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Whatshot sx={{ mr: 1, color: '#ef4444' }} />
          Подтверждение сжигания
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Внимание!</strong> Эта операция необратима.
            </Typography>
          </Alert>
          <Typography variant="body1" paragraph>
            Вы собираетесь сжечь <strong>{formatCurrency(currentBalance)}</strong> токенов SPERM.
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            <strong>Инструкция:</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary" component="div" sx={{ mb: 2 }}>
            1. Отправьте {formatCurrency(currentBalance)} SPERM на нулевой адрес:<br />
            <code style={{ backgroundColor: '#1e293b', padding: '4px 8px', borderRadius: '4px', display: 'inline-block', marginTop: '4px' }}>
              EQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAM9c
            </code><br />
            2. Скопируйте хэш транзакции из тонкипера/эксплорера<br />
            3. Вставьте хэш в поле ниже и подтвердите сжигание
          </Typography>
          <TextField
            fullWidth
            label="Transaction Hash"
            placeholder="Вставьте хэш транзакции сжигания"
            value={transactionHash}
            onChange={(e) => setTransactionHash(e.target.value)}
            disabled={executing}
            required
            sx={{ mb: 2 }}
            helperText="Пример: EQC4yNXijh2EJqDpPunsjjNeLG7VQFdbVH64QIX5aowyX0bh..."
          />
          {errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog(false)}
            disabled={executing}
          >
            Отмена
          </Button>
          <Button
            onClick={handleExecuteBurn}
            variant="contained"
            color="error"
            disabled={executing || !transactionHash || transactionHash.trim() === ''}
            startIcon={<Whatshot />}
          >
            {executing ? 'Выполняется...' : 'Подтвердить сжигание'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог ручного сжигания (автоматическая отправка) */}
      <Dialog
        open={manualBurnDialog}
        onClose={() => !executing && setManualBurnDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Whatshot sx={{ mr: 1, color: '#ef4444' }} />
          Сжечь токены SPERM
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Система автоматически отправит burn транзакцию на блокчейн.
              Вам не нужно делать это вручную.
            </Typography>
          </Alert>

          <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(99, 102, 241, 0.1)', borderRadius: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Доступно на кошельке:
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#6366f1' }}>
              {formatCurrency(walletBalance)} SPERM
            </Typography>
          </Box>

          {/* Галка - списать с burn пула */}
          <Box sx={{
            p: 2,
            mb: 2,
            bgcolor: usePoolAmount ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.05)',
            borderRadius: 2,
            border: usePoolAmount ? '2px solid #ff9800' : '1px solid rgba(255, 152, 0, 0.3)'
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={usePoolAmount}
                  onChange={(e) => {
                    setUsePoolAmount(e.target.checked);
                    if (e.target.checked) {
                      setBurnAmount(currentBalance.toString());
                    } else {
                      setBurnAmount('');
                    }
                  }}
                  disabled={executing || currentBalance === 0}
                  sx={{
                    color: '#ff9800',
                    '&.Mui-checked': { color: '#ff9800' }
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                    🔥 Списать с Burn пула
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Сумма: {formatCurrency(currentBalance)} SPERM (пул обнулится)
                  </Typography>
                </Box>
              }
            />
          </Box>

          <TextField
            fullWidth
            label="Количество токенов для сжигания"
            type="number"
            value={usePoolAmount ? currentBalance.toFixed(2) : burnAmount}
            onChange={(e) => setBurnAmount(e.target.value)}
            disabled={executing || usePoolAmount}
            required
            sx={{ mb: 2 }}
            inputProps={{ min: 0, step: 1 }}
            helperText={usePoolAmount
              ? '✓ Сумма из burn пула (поле заблокировано)'
              : `Введите количество SPERM (макс: ${walletBalance.toFixed(2)})`}
          />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setBurnAmount('5')}
            >
              5 SPERM
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setBurnAmount('100')}
            >
              100 SPERM
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setBurnAmount('1000')}
            >
              1000 SPERM
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => setBurnAmount(walletBalance.toString())}
            >
              ВСЁ ({formatCurrency(walletBalance)})
            </Button>
          </Box>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Внимание!</strong> Эта операция необратима.
              Токены будут сожжены навсегда.
            </Typography>
          </Alert>

          {/* Галка рассылки */}
          <Box sx={{
            p: 2,
            bgcolor: 'rgba(76, 175, 80, 0.1)',
            borderRadius: 2,
            border: '1px solid rgba(76, 175, 80, 0.3)'
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendBroadcast}
                  onChange={(e) => setSendBroadcast(e.target.checked)}
                  disabled={executing}
                  sx={{
                    color: '#4caf50',
                    '&.Mui-checked': { color: '#4caf50' }
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                    📢 Отправить рассылку всем пользователям
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Telegram + модалка (высокий приоритет) + колокольчик
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Прогресс рассылки */}
          {broadcastProgress && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(99, 102, 241, 0.1)', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                📤 Рассылка: {broadcastProgress.sent} / {broadcastProgress.total}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(broadcastProgress.sent / broadcastProgress.total) * 100}
                sx={{ height: 8, borderRadius: 4 }}
              />
              {broadcastProgress.status === 'completed' && (
                <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                  ✅ Рассылка завершена!
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setManualBurnDialog(false)}
            disabled={executing}
          >
            Отмена
          </Button>
          <Button
            onClick={handleManualBurn}
            variant="contained"
            color="error"
            disabled={executing || !burnAmount || parseFloat(burnAmount) <= 0}
            startIcon={<Whatshot />}
          >
            {executing ? 'Сжигаем...' : `Сжечь ${burnAmount || 0} SPERM`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🎉 Праздничный диалог успешного сжигания */}
      <Dialog
        open={successDialog}
        onClose={() => setSuccessDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            border: '2px solid #e94560',
            borderRadius: 4,
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{
          textAlign: 'center',
          py: 4,
          px: 3,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #ff6b6b, #ffa502, #ff6b6b)',
            animation: 'shimmer 2s infinite'
          }
        }}>
          {/* Огненная иконка */}
          <Box sx={{
            width: 100,
            height: 100,
            margin: '0 auto 24px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 40px rgba(238, 90, 36, 0.5)',
            animation: 'pulse 1.5s infinite'
          }}>
            <Whatshot sx={{ fontSize: 60, color: '#fff' }} />
          </Box>

          {/* Заголовок */}
          <Typography variant="h4" sx={{
            color: '#fff',
            fontWeight: 700,
            mb: 1,
            textShadow: '0 0 20px rgba(233, 69, 96, 0.5)'
          }}>
            🔥 СОЖЖЕНО! 🔥
          </Typography>

          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3 }}>
            Токены успешно уничтожены навсегда
          </Typography>

          {/* Количество сожжённых токенов */}
          <Box sx={{
            background: 'rgba(233, 69, 96, 0.2)',
            borderRadius: 3,
            py: 3,
            px: 4,
            mb: 3,
            border: '1px solid rgba(233, 69, 96, 0.3)'
          }}>
            <Typography variant="h2" sx={{
              color: '#e94560',
              fontWeight: 800,
              mb: 1,
              textShadow: '0 0 30px rgba(233, 69, 96, 0.5)'
            }}>
              {burnResult?.amount?.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} SPERM
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              💀 Навечно удалены из обращения
            </Typography>
          </Box>

          {/* TX Hash */}
          {burnResult?.txHash && (
            <Box sx={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: 2,
              p: 2,
              mb: 3
            }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>
                Транзакция в блокчейне:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#4fc3f7',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  cursor: 'pointer',
                  '&:hover': { color: '#81d4fa' }
                }}
                onClick={() => window.open(`https://tonviewer.com/transaction/${burnResult.txHash}`, '_blank')}
              >
                {burnResult.txHash}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', mt: 1 }}>
                👆 Нажми чтобы открыть в Tonviewer
              </Typography>
            </Box>
          )}

          {/* Время */}
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mb: 3 }}>
            ⏰ {burnResult?.timestamp}
          </Typography>

          {/* Мотивирующее сообщение */}
          <Box sx={{
            background: 'linear-gradient(90deg, rgba(76, 175, 80, 0.2), rgba(139, 195, 74, 0.2))',
            borderRadius: 2,
            p: 2,
            mb: 3,
            border: '1px solid rgba(76, 175, 80, 0.3)'
          }}>
            <Typography variant="body1" sx={{ color: '#81c784', fontWeight: 500 }}>
              🚀 Дефляция активна! Каждое сжигание увеличивает ценность SPERM для холдеров!
            </Typography>
          </Box>

          <Button
            variant="contained"
            size="large"
            onClick={() => setSuccessDialog(false)}
            sx={{
              background: 'linear-gradient(135deg, #e94560 0%, #c23a52 100%)',
              px: 6,
              py: 1.5,
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #ff6b8a 0%, #e94560 100%)',
              }
            }}
          >
            Отлично! 🎉
          </Button>
        </Box>
      </Dialog>

      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}
      </style>
    </Box>
  );
};

export default BurnPool;
